import { useState, useEffect, useMemo } from "react";
import { X, ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { projectCategories, formatMonthYear, projectImages } from "@/lib/project";

// Lightbox split-panel condiviso tra homepage e /progetti.
// Props:
//   project: oggetto progetto (o null → chiuso)
//   onClose: handler chiusura
export default function ProgettoLightbox({ project, onClose }) {
  const [index, setIndex] = useState(0);
  const images = useMemo(() => (project ? projectImages(project) : []), [project]);

  // Reset index quando cambia progetto
  useEffect(() => {
    setIndex(0);
  }, [project?.id]);

  // Chiusura con ESC + navigazione frecce
  useEffect(() => {
    if (!project) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && images.length > 1) setIndex((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft" && images.length > 1) setIndex((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [project, images.length, onClose]);

  if (!project || images.length === 0) return null;

  const cats = projectCategories(project);
  const next = () => setIndex((i) => (i + 1) % images.length);
  const prev = () => setIndex((i) => (i - 1 + images.length) % images.length);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2 hover:bg-white/10 rounded-full transition-colors z-20"
        onClick={onClose}
        data-testid="progetto-lightbox-close"
        aria-label="Chiudi"
      >
        <X size={28} />
      </button>

      <div
        className="w-full max-w-6xl h-[92vh] md:h-[85vh] flex flex-col md:flex-row bg-white rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Immagine */}
        <div className="relative flex-1 bg-black flex items-center justify-center min-h-0 md:min-h-full">
          <img
            src={images[index].image_url}
            alt={project.titolo}
            className="max-w-full max-h-full object-contain"
          />

          {images.length > 1 && (
            <>
              <button
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white p-2 rounded-full transition-colors"
                onClick={prev}
                aria-label="Precedente"
              >
                <ChevronLeft size={28} />
              </button>
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white p-2 rounded-full transition-colors"
                onClick={next}
                aria-label="Successiva"
              >
                <ChevronRight size={28} />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full">
                {index + 1} / {images.length}
              </div>
            </>
          )}
        </div>

        {/* Pannello info */}
        <div className="w-full md:w-80 lg:w-96 flex flex-col bg-white border-t md:border-t-0 md:border-l border-[#E2E8F0]">
          <div className="p-6 md:p-8 flex-1 overflow-y-auto">
            <div className="flex flex-wrap gap-1.5 mb-4">
              {cats.map((cat) => (
                <span
                  key={cat}
                  className="inline-block px-3 py-1 bg-[#FF9500] text-white text-xs font-semibold rounded-full"
                >
                  {cat}
                </span>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-[#0B1E36] leading-tight mb-3">
              {project.titolo}
            </h3>

            {project.data_installazione && (
              <p className="flex items-center gap-1.5 text-[#64748B] text-sm mb-5">
                <Calendar className="w-4 h-4" />
                Installato in {formatMonthYear(project.data_installazione)}
              </p>
            )}

            {project.descrizione && (
              <div>
                <h4 className="text-xs uppercase tracking-wider text-[#94A3B8] font-semibold mb-2">
                  Descrizione
                </h4>
                <p className="text-[#334155] text-sm leading-relaxed whitespace-pre-wrap">
                  {project.descrizione}
                </p>
              </div>
            )}
          </div>

          {images.length > 1 && (
            <div className="border-t border-[#E2E8F0] p-4 bg-[#F8FAFC]">
              <div className="flex gap-2 overflow-x-auto">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setIndex(i)}
                    className={`flex-shrink-0 w-14 h-14 rounded-md overflow-hidden border-2 transition-all ${
                      i === index
                        ? "border-[#FF9500]"
                        : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                    aria-label={`Vai a immagine ${i + 1}`}
                  >
                    <img
                      src={img.image_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
